import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, timer, catchError, firstValueFrom, Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';

import { SsoAuthService } from '../../services/apis/sso-auth.service';
import { FcmTokenService } from '../../services/common/fcm-token.service';
import { DeviceKeysService } from '../../services/common/device-keys.service';
import {
  ContactType,
  IDeviceInfo,
  ILoginPayload,
  IVerifyData,
  RecoveryField,
} from '../../interfaces/auth.interface';
import { RoutesMapper } from '../../routes-mapper';
import { SplashScreen } from '../../common-components/splash-screen/splash-screen';

import {
  AppRedirectResult,
  COUNTRIES,
  DEFAULT_COUNTRY_ID,
  EMAIL_PATTERN,
  IContactGroup,
  IRecoveryState,
  IRecoveryStepDef,
  IRestoreState,
  IState,
  ITrustedState,
  PHONE_LENGTH,
  PHONE_PATTERN,
  RECOVERY_PHONE_PATTERN,
  RecoveryStepKey,
  findCountry,
  initialRecoveryState,
  initialRestoreState,
  initialState,
  initialTrustedState,
} from './sso-login.models';
import { ContactStepComponent } from './contact-step/contact-step.component';
import { PasswordStepComponent } from './password-step/password-step.component';
import { ForgotPasswordStepComponent } from './forgot-password-step/forgot-password-step.component';
import { OtpStepComponent } from './otp-step/otp-step.component';
import { RecoverySetupComponent } from './recovery-setup/recovery-setup.component';
import { TrustedDeviceComponent } from './trusted-device/trusted-device.component';
import { RestoreAccountComponent } from './restore-account/restore-account.component';

@Component({
  selector: 'app-sso-login',
  templateUrl: './sso-login.component.html',
  styleUrls: ['./sso-login.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SplashScreen,
    ContactStepComponent,
    PasswordStepComponent,
    ForgotPasswordStepComponent,
    OtpStepComponent,
    RecoverySetupComponent,
    TrustedDeviceComponent,
    RestoreAccountComponent,
  ],
})
export class SsoLoginComponent implements OnInit, OnDestroy {
  @ViewChild('contactDialog') private contactDialog?: ElementRef<HTMLDialogElement>;

  loginForm!: FormGroup;
  forgotPasswordForm!: FormGroup;
  forgotPasswordError = '';
  recoveryForm!: FormGroup;
  state: IState = { ...initialState };
  recovery: IRecoveryState = {
    ...initialRecoveryState,
    done: { ...initialRecoveryState.done },
  };
  trusted: ITrustedState = {
    ...initialTrustedState,
    webDevices: [],
  };
  restore: IRestoreState = { ...initialRestoreState };

  recoverySteps: IRecoveryStepDef[] = [
    { index: 1, key: 'password', label: 'Password' },
    { index: 2, key: 'email', label: 'Email' },
    { index: 3, key: 'phone', label: 'Alternate Number' },
  ];

  countryList: { id: string; title: string }[] = COUNTRIES.map((c) => ({
    id: c.id,
    title: c.title,
  }));

  selectedCountry = new FormControl<string>(DEFAULT_COUNTRY_ID);

  isLoading = false;
  isLoadingSplash = true;
  isLoggingOutToApp = false;
  isPasswordVisible = false;
  copiedContact = '';

  private lat: number | null = null;
  private long: number | null = null;

  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private trustChallenge = '';
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;
  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private authService: SsoAuthService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private fcmTokenService: FcmTokenService,
    private deviceKeys: DeviceKeysService,
  ) {
    this.loginForm = this.fb.group({
      contact: ['', [Validators.required]],
      password: [''],
    });

    this.forgotPasswordForm = this.fb.group({
      otp: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
      password: ['', [Validators.required, Validators.minLength(8)]],
      confirmPassword: ['', [Validators.required]],
    });

    this.recoveryForm = this.fb.group({
      password: ['', [Validators.minLength(8)]],
      confirmPassword: [''],
      email: [''],
      phone: [''],
      otp: [''],
    });

    const contactSub = this.loginForm.get('contact')?.valueChanges.subscribe(() => {
      this.state.contactError = '';
    });

    if (contactSub) {
      this.subscriptions.add(contactSub);
    }
  }

  ngOnInit(): void {
    this.isLoadingSplash = true;
    void this.initializeSession();
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    if (this.copyResetTimer) {
      clearTimeout(this.copyResetTimer);
    }

    this.subscriptions.unsubscribe();
  }

  private capturePendingAppRedirect(): void {
    const qp = new URLSearchParams(window.location.search);
    const appId = qp.get('appId');
    const codeChallenge = qp.get('codeChallenge');
    const appMode = qp.get('appMode');
    const returnUrl = qp.get('returnUrl');

    if (appId && appId !== 'null' && appId !== 'undefined') {
      sessionStorage.setItem('sso_appId', appId);
    }

    if (codeChallenge && codeChallenge !== 'null' && codeChallenge !== 'undefined') {
      sessionStorage.setItem('sso_codeChallenge', codeChallenge);
    }

    if (appMode) {
      sessionStorage.setItem('sso_appMode', appMode);
    }

    if (returnUrl) {
      sessionStorage.setItem('sso_returnUrl', returnUrl);
    }
  }

  private hasPendingAppRedirect(): boolean {
    return !!(
      sessionStorage.getItem('sso_appId') &&
      sessionStorage.getItem('sso_codeChallenge')
    );
  }

  private clearPendingAppRedirect(): void {
    sessionStorage.removeItem('sso_appId');
    sessionStorage.removeItem('sso_codeChallenge');
    sessionStorage.removeItem('sso_appMode');
    sessionStorage.removeItem('sso_returnUrl');
  }

  private async authorizeAndRedirectToApp(): Promise<AppRedirectResult> {
    const appId = sessionStorage.getItem('sso_appId');
    const codeChallenge = sessionStorage.getItem('sso_codeChallenge');
    const appMode = sessionStorage.getItem('sso_appMode');
    const returnUrl = sessionStorage.getItem('sso_returnUrl');

    if (!appId || !codeChallenge) {
      return 'failed';
    }

    try {
      const res = await firstValueFrom(
        this.authService.authorizeSession({
          appId: Number(appId),
          codeChallenge,
        }),
      );

      const accessCode = res?.data?.accessCode;
      const serverRedirectUrl = res?.data?.redirectUrl;

      if (!res.error && accessCode) {
        let target = '';

        if (returnUrl && !environment.production) {
          target = `${returnUrl}?accessCode=${encodeURIComponent(accessCode)}`;
        } else if (appMode === 'true') {
          target = `myapp://sso/callback?accessCode=${encodeURIComponent(accessCode)}`;
        } else if (serverRedirectUrl) {
          target = `${serverRedirectUrl}?accessCode=${encodeURIComponent(accessCode)}`;
        }

        if (target) {
          this.clearPendingAppRedirect();
          window.location.href = target;
          return 'redirected';
        }
      }

      return 'failed';
    } catch (e: any) {
      const status = e?.statusCode;

      if (status === 401 || status === 403) {
        return 'unauthorized';
      }

      return 'failed';
    }
  }

  private async initializeSession(): Promise<void> {
    this.capturePendingAppRedirect();
    if (await this.handleLogoutQueryParam()) {
      return;
    }

    const handled = await this.tryExistingSession();

    if (handled) {
      return;
    }

    const restored = await this.tryTrustedDeviceRestore();

    if (restored) {
      return;
    }

    this.resolveCountryFromLocation();
  }

  private async handleLogoutQueryParam(): Promise<boolean> {
    const qp = new URLSearchParams(window.location.search);

    if (qp.get('logout') !== 'true') {
      return false;
    }

    const hadSession = this.authService.hasSessionFlag();
    const returnUrl = qp.get('returnUrl');
    let logoutSucceeded = false;

    try {
      const response = await firstValueFrom(this.authService.logout());
      logoutSucceeded = !!response && !response.error;
    } catch {}

    this.authService.clearSessionFlag();
    this.authService.clearSelectedCountry();
    this.authService.clearStateToken();

    qp.delete('logout');

    const query = qp.toString();

    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? '?' + query : ''}`,
    );

    if (hadSession) {
      this.toastr.success('Logged out successfully');
    }

    if (logoutSucceeded && returnUrl) {
      try {
        const target = new URL(returnUrl, window.location.origin);

        if (target.protocol === 'https:' || target.protocol === 'http:') {
          this.isLoggingOutToApp = true;
          this.cdr.detectChanges();
          await new Promise<void>((resolve) => window.setTimeout(resolve, 450));
          window.location.href = target.href;
          return true;
        }
      } catch {
        this.isLoggingOutToApp = false;
        this.cdr.detectChanges();
      }
    }

    return false;
  }

  private async tryExistingSession(): Promise<boolean> {
    if (!this.authService.hasSessionFlag()) {
      return false;
    }

    if (this.hasPendingAppRedirect()) {
      const result = await this.authorizeAndRedirectToApp();

      if (result === 'redirected') {
        return true;
      }

      if (result === 'unauthorized') {
        this.authService.clearSessionFlag();
        return false;
      }

      this.clearPendingAppRedirect();
      this.router.navigate([RoutesMapper.DASHBOARD]);
      return true;
    }

    try {
      const res = await firstValueFrom(this.authService.getProfileDetails());

      if (!res.error) {
        this.authService.markSessionActive();
        this.router.navigate([RoutesMapper.DASHBOARD]);
        return true;
      }

      this.authService.clearSessionFlag();
      return false;
    } catch (e: any) {
      const status = e?.statusCode;

      if (status === 401 || status === 403) {
        this.authService.clearSessionFlag();
        return false;
      }

      return false;
    }
  }

  private async tryTrustedDeviceRestore(): Promise<boolean> {
    const deviceId = this.authService.getTrustedDeviceId();

    if (!deviceId) {
      return false;
    }

    if (!(await this.deviceKeys.hasKeyPair())) {
      return false;
    }

    try {
      const res = await firstValueFrom(this.authService.checkDevice(deviceId));
      const d = res?.data;

      if (res.error || !d?.challenge) {
        await this.forgetTrustedDevice();
        return false;
      }

      this.restore = {
        deviceId,
        name: [d.firstName, d.middleName, d.lastName]
          .filter(Boolean)
          .join(' '),
        familyCode: d.familyCode || '',
        challenge: d.challenge,
        error: '',
        loadingAction: null,
      };

      this.state.view = 'restore';
      this.isLoadingSplash = false;
      this.cdr.detectChanges();

      return true;
    } catch {
      await this.forgetTrustedDevice();
      return false;
    }
  }

  async restoreAccount(): Promise<void> {
    if (this.restore.loadingAction) {
      return;
    }

    this.restore.loadingAction = 'restore';
    this.restore.error = '';
    this.cdr.detectChanges();

    try {
      const solution = await this.deviceKeys.solveChallenge(
        this.restore.challenge,
      );

      const res = await firstValueFrom(
        this.authService.loginByTrustedDevice({
          deviceId: this.restore.deviceId,
          solution,
        }),
      );

      this.restore.loadingAction = null;

      if (res.error) {
        this.restore.error =
          res.message ||
          'Could not restore your session. Please log in again';

        this.cdr.detectChanges();
        return;
      }

      if (res.data?.trustedDeviceId) {
        this.authService.setTrustedDeviceId(res.data.trustedDeviceId);
      }

      await this.completeLogin(res.message);
    } catch (e: any) {
      this.restore.loadingAction = null;
      this.restore.error =
        e?.message ||
        'Could not restore your session. Please log in again';

      this.cdr.detectChanges();
    }
  }

  async useAnotherAccount(): Promise<void> {
    if (this.restore.loadingAction) {
      return;
    }

    this.restore.loadingAction = 'revoke';
    this.restore.error = '';
    this.cdr.detectChanges();

    try {
      const solution = await this.deviceKeys.solveChallenge(
        this.restore.challenge,
      );

      await firstValueFrom(
        this.authService.revokeDevice({
          deviceId: this.restore.deviceId,
          solution,
        }),
      );
    } catch {}

    await this.forgetTrustedDevice();

    this.restore = { ...initialRestoreState };
    this.state.view = 'contact';

    this.resolveCountryFromLocation();

    this.cdr.detectChanges();
  }

  private async forgetTrustedDevice(): Promise<void> {
    this.authService.clearTrustedDeviceId();
    this.authService.clearSessionFlag();
    await this.deviceKeys.clear();
  }

  private async completeLogin(message?: string): Promise<void> {
    this.authService.markSessionActive();
    this.authService.clearStateToken();

    this.toastr.success(message || 'Logged in successfully');

    if (this.hasPendingAppRedirect()) {
      const result = await this.authorizeAndRedirectToApp();

      if (result === 'redirected') {
        return;
      }

      this.clearPendingAppRedirect();
    }

    this.router.navigate([RoutesMapper.DASHBOARD]);
  }

  private markDeviceTrusted(data?: IVerifyData | null): void {
    const fromBackend = data?.trustedDeviceId;

    if (!fromBackend && !environment.production) {
      console.warn(
        '[trusted-device] backend did not return trustedDeviceId — ' +
          'falling back to the locally generated sso_device_id. Restore ' +
          'will only work if the backend keys trust on that same id.',
      );
    }

    const deviceId = fromBackend || this.getDeviceInfo().deviceId;

    if (deviceId) {
      this.authService.setTrustedDeviceId(deviceId);
    }
  }

  get activeCountry() {
    return findCountry(this.selectedCountry.value);
  }

  get isEmailMode(): boolean {
    return this.activeCountry?.contactType === 'email';
  }

  get contactGroups(): IContactGroup[] {
    return this.activeCountry?.contactGroups ?? [];
  }

  private resolveCountryFromLocation(): void {
    if (!('geolocation' in navigator)) {
      this.setDefaultCountry(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.lat = position.coords.latitude;
        this.long = position.coords.longitude;

        forkJoin([
          this.authService
            .getCountryByLocation(this.lat, this.long)
            .pipe(catchError(() => of(null))),
          timer(800),
        ]).subscribe(([res]) => {
          const country =
            res && !res.error ? res.data?.country : null;

          this.setDefaultCountry(country ?? null);
        });
      },
      () => {
        this.setDefaultCountry(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 300000,
      },
    );
  }

  private setDefaultCountry(country: string | null): void {
    const match = country
      ? this.countryList.find(
          (c) =>
            c.title.toUpperCase() === country.toUpperCase(),
        )
      : null;

    const countryId = match ? match.id : DEFAULT_COUNTRY_ID;

    this.selectedCountry.setValue(countryId);
    sessionStorage.setItem('sso_country', countryId);

    this.isLoadingSplash = false;
    this.cdr.detectChanges();
  }

  onSelectCountry(countryId: string): void {
    if (this.selectedCountry.value === countryId) {
      return;
    }

    this.selectedCountry.setValue(countryId);
    sessionStorage.setItem('sso_country', countryId);

    this.loginForm.get('contact')?.setValue('');
    this.state.contactError = '';

    this.cdr.detectChanges();
  }

  private getDeviceInfo(): IDeviceInfo {
    let deviceId = localStorage.getItem('sso_device_id');

    if (!deviceId) {
      deviceId =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `web-${Math.random().toString(36).slice(2)}${Date.now()}`;

      localStorage.setItem('sso_device_id', deviceId);
    }

    return {
      deviceId,
      deviceType: 'WEB',
      deviceName: this.getBrowserName(),
      lat: this.lat,
      long: this.long,
    };
  }

  private getBrowserName(): string {
    const ua = navigator.userAgent;

    let browser = 'Browser';

    if (ua.includes('Edg/')) {
      browser = 'Edge';
    } else if (ua.includes('Chrome/')) {
      browser = 'Chrome';
    } else if (ua.includes('Firefox/')) {
      browser = 'Firefox';
    } else if (ua.includes('Safari/')) {
      browser = 'Safari';
    }

    const platform =
      (navigator as any).userAgentData?.platform ||
      navigator.platform ||
      'Web';

    return `${browser} on ${platform}`;
  }

  isValidPhone(value: string | null | undefined): boolean {
    if (!value) {
      return false;
    }

    return PHONE_PATTERN.test(value.trim());
  }

  isValidRecoveryPhone(value: string | null | undefined): boolean {
    if (!value) {
      return false;
    }

    return RECOVERY_PHONE_PATTERN.test(value.trim());
  }

  isValidEmail(value: string | null | undefined): boolean {
    if (!value) {
      return false;
    }

    return EMAIL_PATTERN.test(value.trim());
  }

  isValidContact(): boolean {
    if (!this.activeCountry) {
      return false;
    }

    const val = this.loginForm.get('contact')?.value;

    return this.isEmailMode
      ? this.isValidEmail(val)
      : this.isValidPhone(val);
  }

  isButtonDisabled(): {
    text: string;
    isDisabled: boolean;
  } {
    if (!this.activeCountry) {
      return {
        text: 'Select country/region',
        isDisabled: true,
      };
    }

    if (!this.loginForm.get('contact')?.value) {
      return {
        text: this.isEmailMode
          ? 'Enter email'
          : 'Enter phone number',
        isDisabled: true,
      };
    }

    if (!this.isValidContact()) {
      return {
        text: this.isEmailMode
          ? 'Enter a valid email'
          : `Enter a valid ${PHONE_LENGTH} digit phone number`,
        isDisabled: true,
      };
    }

    return {
      text: 'Login',
      isDisabled: false,
    };
  }

  togglePassword(): void {
    this.isPasswordVisible = !this.isPasswordVisible;
  }

  onSubmit(): void {
    if (this.isLoading) {
      return;
    }

    const contact = (
      this.loginForm.get('contact')?.value || ''
    ).trim();

    const country = this.selectedCountry.value;

    if (!country) {
      this.state.contactError =
        'Please select your country/region first';
      return;
    }

    if (!this.isValidContact()) {
      this.state.contactError = this.isEmailMode
        ? 'Please enter a valid email'
        : `Please enter a valid ${PHONE_LENGTH} digit phone number`;

      return;
    }

    sessionStorage.setItem('sso_country', country);

    const payload: ILoginPayload = {
      contact,
      country,
      deviceInfo: this.getDeviceInfo(),
    };

    this.isLoading = true;

    this.authService.login(payload).subscribe({
      next: (res) => {
        this.isLoading = false;

        if (res.error || !res.data) {
          this.state.contactError =
            res.message || 'Unable to login';

          this.cdr.detectChanges();
          return;
        }

        this.authService.setStateToken(res.data.stateToken);

        if (res.data.passwordEnabled === true) {
          this.state.view = 'password';
          this.state.passwordError = '';
          this.loginForm.get('password')?.reset();
        } else if (res.data.isOtpSent) {
          this.state.view = 'otp';
          this.state.otp = '';
          this.state.otpError = '';
          this.state.otpSentTo =
            res.data.contact || contact;

          this.startCountdown();

          this.toastr.success(
            res.message || 'OTP sent successfully',
          );
        } else {
          this.toastr.error(
            res.message || 'Unexpected response from server',
          );
        }

        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;

        this.state.contactError =
          err?.message ||
          'Something went wrong. Please try again.';

        this.cdr.detectChanges();
      },
    });
  }

  onLocationCaptured(coords: {
    lat: number;
    lng: number;
  }): void {
    this.lat = coords.lat;
    this.long = coords.lng;
  }

  onOtpChange(value: string): void {
    this.state.otp = value;
    this.state.otpError = '';
  }

  validateOtp(): boolean {
    if (!this.state.otp) {
      this.state.otpError = 'OTP is required';
      return false;
    }

    if (!/^\d+$/.test(this.state.otp)) {
      this.state.otpError =
        'OTP must contain only numbers';
      return false;
    }

    if (this.state.otp.length !== 6) {
      this.state.otpError =
        'OTP must be exactly 6 digits';
      return false;
    }

    this.state.otpError = '';
    return true;
  }

  verifyOtp(): void {
    if (this.isLoading || !this.validateOtp()) {
      return;
    }

    this.isLoading = true;

    this.authService
      .verifyOtp({
        otp: this.state.otp,
      })
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          if (res.error) {
            this.state.otpError =
              res.message || 'OTP verification failed';

            this.cdr.detectChanges();
            return;
          }

          this.handleVerifyResponse(res);
        },
        error: (err) => {
          this.isLoading = false;

          this.state.otpError =
            err?.message || 'OTP verification failed';

          this.cdr.detectChanges();
        },
      });
  }

  resendOtp(): void {
    if (this.isLoading) {
      return;
    }

    const contact = (
      this.loginForm.get('contact')?.value || ''
    ).trim();

    const country = this.selectedCountry.value;

    if (!country) {
      return;
    }

    sessionStorage.setItem('sso_country', country);

    const payload: ILoginPayload = {
      contact,
      country,
      deviceInfo: this.getDeviceInfo(),
    };

    this.state.otp = '';
    this.state.otpError = '';

    this.isLoading = true;

    this.authService.login(payload).subscribe({
      next: (res) => {
        this.isLoading = false;

        if (
          res.error ||
          !res.data ||
          !res.data.isOtpSent
        ) {
          this.state.otpError =
            res.message ||
            'Unable to resend OTP. Please try again.';

          this.cdr.detectChanges();
          return;
        }

        this.authService.setStateToken(
          res.data.stateToken,
        );

        this.state.otpSentTo =
          res.data.contact || contact;

        this.startCountdown();

        this.toastr.success(
          res.message || 'OTP sent successfully',
        );

        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;

        this.state.otpError =
          err?.message ||
          'Unable to resend OTP. Please try again.';

        this.cdr.detectChanges();
      },
    });
  }

  private startCountdown(): void {
    this.state.countdown = 60;

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }

    this.countdownInterval = setInterval(() => {
      if (
        this.state.countdown > 0 &&
        this.state.view === 'otp'
      ) {
        this.state.countdown--;
        this.cdr.detectChanges();
      } else if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
    }, 1000);
  }

  verifyPassword(): void {
    if (this.isLoading) {
      return;
    }

    const password =
      this.loginForm.get('password')?.value;

    if (!password) {
      this.state.passwordError =
        'Please enter your password';
      return;
    }

    this.isLoading = true;

    this.authService
      .verifyPassword({ password })
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          if (res.error) {
            this.state.passwordError =
              res.message || 'Invalid password';

            this.cdr.detectChanges();
            return;
          }

          this.handleVerifyResponse(res);
        },
        error: (err) => {
          this.isLoading = false;

          this.state.passwordError =
            err?.message || 'Invalid password';

          this.cdr.detectChanges();
        },
      });
  }

  requestForgotPasswordOtp(): void {
    if (this.isLoading || this.selectedCountry.value !== 'NEPAL' ||
      (this.state.view !== 'password' && this.state.view !== 'forgotPassword')) {
      return;
    }

    const contact = (this.loginForm.get('contact')?.value || '').trim();
    if (!contact) {
      this.state.passwordError = 'Please enter your contact number first';
      return;
    }

    this.isLoading = true;
    this.state.passwordError = '';
    this.forgotPasswordError = '';

    this.authService.sendForgotPasswordOtp({
      contact,
      country: 'NEPAL',
      contactType: ContactType.PHONE,
      deviceInfo: this.getDeviceInfo(),
    }).subscribe({
      next: (res) => {
        this.isLoading = false;

        if (res.error) {
          const message = res.message || 'Unable to send OTP. Please try again.';
          if (this.state.view === 'forgotPassword') {
            this.forgotPasswordError = message;
          } else {
            this.state.passwordError = message;
          }
          this.cdr.detectChanges();
          return;
        }

        this.authService.setStateToken(res.data?.stateToken);
        if (this.state.view !== 'forgotPassword') {
          this.forgotPasswordForm.reset();
          this.state.view = 'forgotPassword';
        } else {
          this.forgotPasswordForm.get('otp')?.reset();
        }

        this.toastr.success(res.message || 'OTP sent successfully');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        const message = err?.message || 'Unable to send OTP. Please try again.';
        if (this.state.view === 'forgotPassword') {
          this.forgotPasswordError = message;
        } else {
          this.state.passwordError = message;
        }
        this.cdr.detectChanges();
      },
    });
  }

  updateForgotPassword(): void {
    if (this.isLoading || this.selectedCountry.value !== 'NEPAL' ||
      this.state.view !== 'forgotPassword') {
      return;
    }

    const otp = (this.forgotPasswordForm.get('otp')?.value || '').trim();
    const newPassword = this.forgotPasswordForm.get('password')?.value || '';
    const confirmPassword = this.forgotPasswordForm.get('confirmPassword')?.value || '';

    if (!/^\d{6}$/.test(otp)) {
      this.forgotPasswordError = 'Enter the 6-digit OTP';
      return;
    }
    if (newPassword.length < 8) {
      this.forgotPasswordError = 'Password must be at least 8 characters';
      return;
    }
    if (newPassword !== confirmPassword) {
      this.forgotPasswordError = 'Passwords do not match';
      return;
    }

    this.isLoading = true;
    this.forgotPasswordError = '';

    this.authService.verifyForgotPasswordOtp({ otp, updatedPassword: newPassword }).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.error) {
          this.forgotPasswordError = res.message || 'Unable to update password. Please try again.';
          this.cdr.detectChanges();
          return;
        }

        this.authService.clearStateToken();
        this.goBack();
        this.forgotPasswordForm.reset();
        this.forgotPasswordError = '';
        this.toastr.success(res.message || 'Password updated. Please log in.');
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        this.forgotPasswordError = err?.message || 'Unable to update password. Please try again.';
        this.cdr.detectChanges();
      },
    });
  }

  backFromForgotPassword(): void {
    if (this.isLoading) return;
    this.forgotPasswordForm.reset();
    this.forgotPasswordError = '';
    this.authService.clearStateToken();
    this.goBack();
  }

  private handleVerifyResponse(res: {
    message?: string;
    data?: IVerifyData | null;
  }): void {
    const data: IVerifyData = res?.data || {};

    this.authService.setStateToken(data.stateToken);

    if (data.showSecurityQuestions) {
      this.toastr.info(
        'Security questions flow — coming soon',
      );
      return;
    }

    if (data.showSetRecovery) {
      this.recovery = {
        ...initialRecoveryState,
        done: { ...initialRecoveryState.done },
      };

      this.recoveryForm.reset();
      this.state.view = 'recovery';

      this.toastr.info(
        res.message ||
          'Please provide at least one of password, alternate mobile number or email, to secure your account',
      );

      this.cdr.detectChanges();
      return;
    }

    if (data.showAskTrustedDevice) {
      this.trusted = {
        ...initialTrustedState,
        primaryDeviceId:
          data.primaryDeviceId || '',
        primaryDeviceName:
          data.primaryDeviceName ||
          'your primary device',
        primaryDeviceLocation:
          data.primaryDeviceLocation || '',
        limitReached: !!data.limitReached,
        webDevices: data.webDevices || [],
      };

      this.state.view = 'trusted';

      this.cdr.detectChanges();
      return;
    }

    void this.completeLogin(res.message);
  }

  continueUntrusted(): void {
    if (this.trusted.loadingAction) {
      return;
    }

    this.trusted.loadingAction = 'untrusted';
    this.trusted.error = '';

    this.authService.continueUntrusted().subscribe({
      next: (res) => {
        this.trusted.loadingAction = null;

        if (res.error) {
          this.trusted.error =
            res.message ||
            'Unable to continue. Please try again';

          this.cdr.detectChanges();
          return;
        }

        this.handleVerifyResponse(res);
      },
      error: (err) => {
        this.trusted.loadingAction = null;

        this.trusted.error =
          err?.message ||
          'Unable to continue. Please try again';

        this.cdr.detectChanges();
      },
    });
  }

  async trustThisDevice(): Promise<void> {
    if (this.trusted.loadingAction) {
      return;
    }

    this.trusted.error = '';

    if (
      this.trusted.limitReached &&
      this.trusted.webDevices.length > 0
    ) {
      this.trusted.stage = 'selectDevice';
      this.trusted.selectedRemoveDeviceId =
        this.trusted.webDevices[0]?.deviceId || '';

      this.cdr.detectChanges();
      return;
    }

    await this.startTrustDeviceRequest();
  }

  selectDeviceToRemove(deviceId: string): void {
    this.trusted.selectedRemoveDeviceId = deviceId;
  }

  async confirmDeviceRemovalAndTrust(): Promise<void> {
    if (this.trusted.loadingAction) {
      return;
    }

    if (!this.trusted.selectedRemoveDeviceId) {
      this.trusted.error =
        'Please select a device to log out';
      return;
    }

    await this.startTrustDeviceRequest(
      this.trusted.selectedRemoveDeviceId,
    );
  }

  cancelDeviceSelection(): void {
    this.trusted.stage = 'ask';
    this.trusted.error = '';
  }

  private async startTrustDeviceRequest(
    removeDeviceId?: string,
  ): Promise<void> {
    this.trusted.loadingAction = 'trust';
    this.trusted.error = '';
    this.cdr.detectChanges();

    try {
      const publicKey =
        await this.deviceKeys.rotateAndGetPublicKeyPem();

      const fcmToken =
        await this.fcmTokenService.getToken();

      this.authService
        .addTrustedDevice({
          fcmToken,
          publicKey,
          ...(removeDeviceId
            ? { removeDeviceId }
            : {}),
        })
        .subscribe({
          next: async (res) => {
            if (res.error) {
              this.trusted.loadingAction = null;

              this.trusted.error =
                res.message ||
                'Unable to trust this device. Please try again';

              this.trusted.stage =
                this.trusted.limitReached
                  ? 'selectDevice'
                  : 'ask';

              this.cdr.detectChanges();
              return;
            }

            this.trustChallenge = res.data?.challenge || '';

            if (!this.trustChallenge) {
              await this.deviceKeys.promotePendingKeyPair();
              this.markDeviceTrusted(res.data);

              this.trusted.loadingAction = null;

              this.handleVerifyResponse(res);
              return;
            }

            if (res.data?.isPushNotificationSent) {
              this.trusted.loadingAction = null;
              this.trusted.stage = 'code';
              this.trusted.code = '';

              this.cdr.detectChanges();
            } else {
              await this.submitReviewApproval(null);
            }
          },
          error: (err) => {
            this.trusted.loadingAction = null;

            this.trusted.error =
              err?.message ||
              'Unable to trust this device. Please try again';

            this.cdr.detectChanges();
          },
        });
    } catch {
      this.trusted.loadingAction = null;

      this.trusted.error =
        'Could not prepare this device for trust. Please try again';

      this.cdr.detectChanges();
    }
  }

  async verifyTrustCode(): Promise<void> {
    if (this.trusted.loadingAction) {
      return;
    }

    if (!/^\d{6}$/.test(this.trusted.code)) {
      this.trusted.error =
        'Enter the 6-digit code shown on your primary device';

      return;
    }

    this.trusted.error = '';
    this.trusted.loadingAction = 'verify';

    this.cdr.detectChanges();

    await this.submitReviewApproval(
      this.trusted.code,
    );
  }

  private async submitReviewApproval(
    approvalCode: string | null,
  ): Promise<void> {
    if (!this.trustChallenge) {
      this.trusted.loadingAction = null;
      this.trusted.error =
        'Session expired. Please try again';
      this.trusted.stage = 'ask';

      this.cdr.detectChanges();
      return;
    }

    let solvedCode: string;

    try {
      solvedCode =
        await this.deviceKeys.solvePendingChallenge(
          this.trustChallenge,
        );
    } catch (e) {
      console.error(
        '[trusted-device] challenge decrypt failed',
        e,
      );

      this.trusted.loadingAction = null;
      this.trusted.error =
        'Could not verify this device. Please try again';

      this.cdr.detectChanges();
      return;
    }

    this.authService
      .reviewApproval({
        solvedCode,
        ...(approvalCode
          ? { approvalCode }
          : {}),
      })
      .subscribe({
        next: async (res) => {
          this.trusted.loadingAction = null;

          if (res.error) {
            this.trusted.error =
              res.message ||
              'Verification failed. Please try again';

            this.cdr.detectChanges();
            return;
          }

          await this.deviceKeys.promotePendingKeyPair();
          this.markDeviceTrusted(res.data);

          this.trustChallenge = '';

          this.handleVerifyResponse(res);
        },
        error: (err) => {
          this.trusted.loadingAction = null;

          this.trusted.error =
            err?.message ||
            'Verification failed. Please try again';

          this.cdr.detectChanges();
        },
      });
  }

  async resendTrustCode(): Promise<void> {
    if (this.trusted.loadingAction) {
      return;
    }

    this.trusted.code = '';
    this.trusted.error = '';
    this.trustChallenge = '';

    await this.startTrustDeviceRequest(
      this.trusted.selectedRemoveDeviceId ||
        undefined,
    );
  }

  cancelTrustCode(): void {
    if (this.trusted.loadingAction) {
      return;
    }

    this.trusted.stage = this.trusted.limitReached
      ? 'selectDevice'
      : 'ask';

    this.trusted.code = '';
    this.trusted.error = '';
    this.trustChallenge = '';
  }

  currentRecoveryKey(): RecoveryStepKey {
    return this.recoverySteps.find(
      (s) => s.index === this.recovery.step,
    )!.key;
  }

  anyRecoverySet(): boolean {
    return (
      this.recovery.done.password ||
      this.recovery.done.email ||
      this.recovery.done.phone
    );
  }

  goToRecoveryStep(step: number): void {
    if (
      this.isLoading ||
      this.recovery.otpStage
    ) {
      return;
    }

    this.recovery.step = step;
    this.recovery.error = '';

    this.recoveryForm.get('otp')?.reset();
  }

  skipRecoveryStep(): void {
    if (this.isLoading) {
      return;
    }

    this.recovery.otpStage = false;
    this.recovery.error = '';

    this.recoveryForm.get('otp')?.reset();

    if (this.recovery.step < 3) {
      this.recovery.step++;
    }
  }

  setRecoveryPassword(): void {
    if (this.isLoading) {
      return;
    }

    const password = (
      this.recoveryForm.get('password')?.value ||
      ''
    ).trim();

    const confirmPassword = (
      this.recoveryForm.get('confirmPassword')?.value ||
      ''
    ).trim();

    if (!password || password.length < 8) {
      this.recovery.error =
        'Password must be at least 8 characters';
      return;
    }

    if (password !== confirmPassword) {
      this.recovery.error =
        'Passwords do not match';
      return;
    }

    const payload = {
      field: 'PASSWORD' as RecoveryField,
      fieldValue: password,
      confirmFieldValue: confirmPassword,
    };

    this.isLoading = true;

    this.authService
      .recoverySetup(payload)
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          this.authService.setStateToken(
            res?.data?.stateToken,
          );

          this.recovery.done.password = true;
          this.recovery.error = '';

          this.toastr.success(
            res.message ||
              'Password set successfully',
          );

          if (this.recovery.step < 3) {
            this.recovery.step++;
          }

          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;

          this.recovery.error =
            err?.message ||
            'Failed to set password';

          this.cdr.detectChanges();
        },
      });
  }

  sendRecoveryOtp(): void {
    if (this.isLoading) {
      return;
    }

    const key = this.currentRecoveryKey();

    let field: RecoveryField;
    let fieldValue: string;

    if (key === 'email') {
      fieldValue = (
        this.recoveryForm.get('email')?.value ||
        ''
      ).trim();

      if (!this.isValidEmail(fieldValue)) {
        this.recovery.error =
          'Please enter a valid email';
        return;
      }

      field = 'EMAIL';
    } else {
      fieldValue = (
        this.recoveryForm.get('phone')?.value ||
        ''
      ).trim();

      if (!this.isValidRecoveryPhone(fieldValue)) {
        this.recovery.error =
          'Please enter a valid phone number';
        return;
      }

      field = 'ALTERNATE_CONTACT_NUMBER';
    }

    this.isLoading = true;

    this.authService
      .recoverySetup({
        field,
        fieldValue,
      })
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          this.authService.setStateToken(
            res?.data?.stateToken,
          );

          this.recovery.otpStage = true;
          this.recovery.otpSentTo =
            res?.data?.contact || fieldValue;

          this.recovery.error = '';

          this.recoveryForm.get('otp')?.reset();

          this.toastr.success(
            res.message ||
              'OTP sent successfully',
          );

          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;

          this.recovery.error =
            err?.message ||
            'Failed to send OTP';

          this.cdr.detectChanges();
        },
      });
  }

  verifyRecoveryOtp(): void {
    if (this.isLoading) {
      return;
    }

    const otp = (
      this.recoveryForm.get('otp')?.value ||
      ''
    ).trim();

    if (!/^\d{6}$/.test(otp)) {
      this.recovery.error =
        'OTP must be exactly 6 digits';
      return;
    }

    const key = this.currentRecoveryKey();

    const field: RecoveryField =
      key === 'email'
        ? 'EMAIL'
        : 'ALTERNATE_CONTACT_NUMBER';

    this.isLoading = true;

    this.authService
      .recoverySetupVerify({
        field,
        otp,
      })
      .subscribe({
        next: (res) => {
          this.isLoading = false;

          this.authService.setStateToken(
            res?.data?.stateToken,
          );

          if (key === 'email') {
            this.recovery.done.email = true;
          } else {
            this.recovery.done.phone = true;
          }

          this.recovery.otpStage = false;
          this.recovery.otpSentTo = '';
          this.recovery.error = '';

          this.recoveryForm.get('otp')?.reset();

          this.toastr.success(
            res.message ||
              'Verified successfully',
          );

          if (this.recovery.step < 3) {
            this.recovery.step++;
          }

          this.cdr.detectChanges();
        },
        error: (err) => {
          this.isLoading = false;

          this.recovery.error =
            err?.message ||
            'OTP verification failed';

          this.cdr.detectChanges();
        },
      });
  }

  cancelRecoveryOtp(): void {
    this.recovery.otpStage = false;
    this.recovery.otpSentTo = '';
    this.recovery.error = '';

    this.recoveryForm.get('otp')?.reset();
  }

  finishRecoverySetup(): void {
    if (
      this.isLoading ||
      !this.anyRecoverySet()
    ) {
      return;
    }

    this.isLoading = true;

    this.authService
      .completeRecoverySetup()
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          this.handleVerifyResponse(res);
        },
        error: (err) => {
          this.isLoading = false;

          this.recovery.error =
            err?.message ||
            'Failed to complete recovery setup';

          this.cdr.detectChanges();
        },
      });
  }

  goBack(): void {
    const contact =
      this.loginForm.get('contact')?.value;

    this.state = { ...initialState };

    this.recovery = {
      ...initialRecoveryState,
      done: { ...initialRecoveryState.done },
    };

    this.trusted = {
      ...initialTrustedState,
      webDevices: [],
    };

    this.restore = {
      ...initialRestoreState,
    };

    this.trustChallenge = '';

    this.loginForm.reset({
      contact,
      password: '',
    });

    this.recoveryForm.reset();

    this.isPasswordVisible = false;

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  copyContact(value: string): void {
    const markCopied = () => {
      this.copiedContact = value;

      if (this.copyResetTimer) {
        clearTimeout(this.copyResetTimer);
      }

      this.copyResetTimer = setTimeout(() => {
        this.copiedContact = '';
        this.cdr.detectChanges();
      }, 2200);

      this.cdr.detectChanges();
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(value)
        .then(markCopied)
        .catch(() => {
          this.legacyCopy(value);
          markCopied();
        });
    } else {
      this.legacyCopy(value);
      markCopied();
    }
  }

  openContactSheet(): void {
    this.contactDialog?.nativeElement.showModal();
  }

  closeContactSheet(): void {
    this.contactDialog?.nativeElement.close();
  }

  onContactSheetClick(event: MouseEvent): void {
    const dialog = this.contactDialog?.nativeElement;
    if (!dialog) return;

    const bounds = dialog.getBoundingClientRect();
    if (
      event.clientX < bounds.left || event.clientX > bounds.right ||
      event.clientY < bounds.top || event.clientY > bounds.bottom
    ) {
      dialog.close();
    }
  }

  private legacyCopy(value: string): void {
    const el = document.createElement('textarea');

    el.value = value;
    el.setAttribute('readonly', '');
    el.style.position = 'fixed';
    el.style.opacity = '0';

    document.body.appendChild(el);

    el.select();
    document.execCommand('copy');

    document.body.removeChild(el);
  }
}
